namespace Solovis.Reports.ReportHelpers.AttributionReport {

  export interface ResolvedAttributionInterval {
    fromDayId: number | null;
    toDayId: number;
  }

  const YEARS_BACK_BY_PERIOD = {
    oneYear: 1,
    threeYears: 3,
    fiveYears: 5,
    sevenYears: 7,
    tenYears: 10
  };

  function getYearsBackStartDayId(yearsBack: number, asOfDayId: number): number {
    let dayId = asOfDayId;
    const monthsBack = yearsBack * 12;
    for (let i = 0; i < monthsBack; i++) {
      dayId = InvData.Util.DateIndexHelper.getDateIndexOfLastDayInPreviousMonth(dayId);
    }
    return dayId;
  }

  /**
   * Defaults a null or undefined "asOfDate" to today. Called by both parameterMaps in
   * AttributionReport.js: the table parameterMap feeds the result into
   * resolveAttributionTimePeriod below, and the KPI parameterMap passes it straight through.
   * Neither ever sees a missing asOfDayId -- that only holds because DataSourceCollection passes
   * each parameterMap's mapped output through, not because of anything in this function alone.
   *
   * Only null/undefined are treated as blank, matching how the date-index parameter control emits
   * a cleared value. 0 is a valid dayId and is passed through unchanged.
   */
  export function resolveAsOfDayId(asOfDate: number | null | undefined): number {
    return asOfDate ?? InvData.Util.DateIndexHelper.getDateIndexForToday();
  }

  /**
   * Fills a blank "timePeriod" with the parameter's own default (the Edit Time Periods dialog's
   * DEFAULT_PERIOD_CODE). Called by both parameterMaps in AttributionReport.js before either
   * resolver runs, so the KPI tiles and the table resolve the same interval.
   *
   * Blank is defined by isBlankTimePeriod. A saved report with a blank Time Period, or Since Dates
   * with no usable dates, therefore runs on the default period.
   *
   * Since Dates is returned with sinceDayIds filtered to real dayIds, so every consumer sees the
   * same dates (the KPI resolver in portfolio-ui does not filter them itself). Any other value,
   * including an unrecognized period code, is returned as given.
   */
  export function resolveTimePeriod(
    timePeriod: Solovis.Reports.Parameters.AttributionReport.AttributionTimePeriodOptions | null | undefined
  ): Solovis.Reports.Parameters.AttributionReport.AttributionTimePeriodOptions {
    if (isBlankTimePeriod(timePeriod)) {
      return { selectedPeriod: Solovis.Reports.Parameters.AttributionReport.DEFAULT_PERIOD_CODE };
    }
    return timePeriod.selectedPeriod === "sinceDates"
      ? { ...timePeriod, sinceDayIds: getSinceDayIds(timePeriod) }
      : timePeriod;
  }

  // sinceDayIds comes from saved JSON, so it is not guaranteed to be an array of real dayIds.
  function getSinceDayIds(
    timePeriod: Solovis.Reports.Parameters.AttributionReport.AttributionTimePeriodOptions
  ): number[] {
    const sinceDayIds: unknown = timePeriod.sinceDayIds;
    return Array.isArray(sinceDayIds) ? sinceDayIds.filter((d) => typeof d === "number" && isFinite(d)) : [];
  }

  /**
   * The single definition of a blank Time Period: no timePeriod, no selectedPeriod, or Since Dates
   * with no valid dates. Shared by resolveTimePeriod and resolveAttributionTimePeriod so the two
   * cannot drift.
   */
  function isBlankTimePeriod(
    timePeriod: Solovis.Reports.Parameters.AttributionReport.AttributionTimePeriodOptions | null | undefined
  ): boolean {
    return !timePeriod || !timePeriod.selectedPeriod
      || (timePeriod.selectedPeriod === "sinceDates" && getSinceDayIds(timePeriod).length === 0);
  }

  /**
   * Resolves the Attribution Report's "timePeriod" parameter (AttributionTimePeriodOptions, AR-010)
   * plus "asOfDate" into a concrete fromDayId/toDayId range for an IntervalStats request.
   *
   * Pure client-side date math using InvData.Util.DateIndexHelper -- the same primitives
   * MultiLevelAttribution.js already uses for MTD/QTD/YTD/years-back requests. "Since Inception"
   * resolves to fromDayId: null, matching InvData.Analysis.IntervalSetGenerator's own convention for
   * an open-ended lower bound (server-side resolves the actual earliest available data).
   *
   * "Since Dates" allows selecting more than one date in the Edit Time Periods dialog, but this
   * report only ever shows a single resolved interval -- the earliest selected date is used as the
   * lower bound. A later story needing one column/interval per Since Date would be a larger change
   * to the dataSource/table shape, not just this resolver.
   */
  export function resolveAttributionTimePeriod(
    timePeriod: Solovis.Reports.Parameters.AttributionReport.AttributionTimePeriodOptions,
    asOfDayId: number,
    fiscalYearStartMonth: number
  ): ResolvedAttributionInterval | null {
    if (isBlankTimePeriod(timePeriod) || !asOfDayId) {
      return null;
    }

    const period = timePeriod.selectedPeriod;
    const DateIndexHelper = InvData.Util.DateIndexHelper;

    switch (period) {
      case "mtd":
        return { fromDayId: DateIndexHelper.getDateIndexOfLastDayInPreviousMonth(asOfDayId), toDayId: asOfDayId };
      case "qtd":
        return { fromDayId: DateIndexHelper.getDateIndexOfLastDayInPreviousQuarter(asOfDayId), toDayId: asOfDayId };
      case "ytd":
        return { fromDayId: DateIndexHelper.getDateIndexOfLastDayInPreviousYear(asOfDayId), toDayId: asOfDayId };
      case "fytd":
        return { fromDayId: DateIndexHelper.getDateIndexOfLastDayInPreviousFiscalYear(asOfDayId, fiscalYearStartMonth), toDayId: asOfDayId };
      case "sinceInception":
        return { fromDayId: null, toDayId: asOfDayId };
      case "sinceDates": {
        return { fromDayId: Math.min(...getSinceDayIds(timePeriod)), toDayId: asOfDayId };
      }
      default: {
        const yearsBack = YEARS_BACK_BY_PERIOD[period];
        if (!yearsBack) {
          return null;
        }
        return { fromDayId: getYearsBackStartDayId(yearsBack, asOfDayId), toDayId: asOfDayId };
      }
    }
  }
}
